import { StrokedText } from "@/components/ui/text/stroked-text";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { InventoryPokemonsOutput } from "types/web-app";
import { useEffect, useRef, useState } from "react";
import { POKE_DATA } from "game/data/pokemon/data";
import { PokeNames } from "types/pokemon/poke-names";
import classNames from "classnames";
import { getPokemonStats } from "@/utils/pokemon-stats-utils";

type InventoryPokemon = InventoryPokemonsOutput["player_pokemons"][0];

const typeColors: Record<string, string> = {
  normal: "#a8a878",
  fire: "#f08030",
  water: "#6890f0",
  electric: "#f8d030",
  grass: "#78c850",
  ice: "#98d8d8",
  fighting: "#c03028",
  poison: "#a040a0",
  ground: "#e0c068",
  flying: "#a890f0",
  psychic: "#f85888",
  bug: "#a8b820",
  rock: "#b8a038",
  ghost: "#705898",
  dragon: "#7038f8",
  dark: "#705848",
  steel: "#b8b8d0",
  fairy: "#ee99ac",
};

export const TCGPokeItem = ({
  pokemon,
  id,
  level,
  selected,
  isInTeam,
  onClick,
  onLongPress,
  boxIcon,
  boxColor,
}: {
  pokemon: InventoryPokemon;
  id: string;
  level: number;
  selected: boolean;
  isInTeam?: boolean;
  onClick: () => void;
  onLongPress?: () => void;
  talents?: any[];
  boxIcon?: string | undefined;
  boxColor?: string | undefined;
}) => {
  const [isPressing, setIsPressing] = useState(false);
  const timerRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearTimer();
  }, []);

  const startPress = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!onLongPress) return;
    setIsPressing(true);
    longPressFiredRef.current = false;
    startPosRef.current = { x: e.clientX, y: e.clientY };

    timerRef.current = window.setTimeout(() => {
      longPressFiredRef.current = true;
      setIsPressing(false);
      onLongPress?.();
    }, 500);
  };

  const endPress = () => {
    setIsPressing(false);
    clearTimer();
  };

  const cancelIfMovedTooMuch = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isPressing || !startPosRef.current) return;
    const dx = Math.abs(e.clientX - startPosRef.current.x);
    const dy = Math.abs(e.clientY - startPosRef.current.y);
    const tolerance = 10;
    if (dx > tolerance || dy > tolerance) {
      endPress();
    }
  };

  const handleClick = () => {
    if (longPressFiredRef.current) return;
    onClick?.();
  };

  const pokeData = POKE_DATA[pokemon.base_pokemon_name as PokeNames];
  const primaryType = pokeData?.types[0]?.toLowerCase() || "normal";
  const bgImage = `/assets/backgrounds/spawn/${primaryType}.webp`;

  const stats = getPokemonStats(pokemon, 1) ?? {
    resistance: 0,
    ability_power: 0,
    speed: 0,
  };

  const StatBox = ({
    label,
    value,
    icon,
  }: {
    label: string;
    value: number;
    icon?: React.ReactNode;
  }) => (
    <div className="flex flex-col relative items-center bg-black/40 rounded-md p-1 border border-white/20 min-w-[50px] backdrop-blur-sm">
      <StrokedText
        className="text-xs font-bold text-gray-200 absolute"
        strokeColor="#000"
        strokeWidth={3}
        fontSize={10}
      >
        {label}
      </StrokedText>

      <span className="text-sm font-bold text-white drop-shadow-md mt-2">
        {value.toFixed(0)}
      </span>
    </div>
  );

  return (
    <div
      className={classNames(
        "relative flex flex-col p-1 rounded-xl shadow-xl cursor-pointer select-none overflow-hidden group border-2 border-gray-800",
        {
          "ring-4 ring-offset-2 ring-pk-orange-4": selected,
          "opacity-50 grayscale cursor-not-allowed": isInTeam,
        },
      )}
      style={{
        width: "100%",
        maxWidth: "240px",
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      onPointerDown={startPress}
      onPointerUp={endPress}
      onPointerCancel={endPress}
      onPointerLeave={endPress}
      onPointerMove={cancelIfMovedTooMuch}
      onClick={handleClick}
      onContextMenu={(e) => e.preventDefault()}
    >
      {isInTeam && (
        <div className="absolute top-2 right-2 z-40">
          <div className="bg-pk-orange-4 text-white text-[10px] font-bold px-2 py-0.5 rounded-md shadow-cartoon-sm">
            MAIN TEAM
          </div>
        </div>
      )}

      {boxIcon && (
        <div
          className="absolute  top-8 right-0 z-40 p-1 rounded-l-lg border-b-2 border-l-2 border-white shadow-cartoon-sm"
          style={{ backgroundColor: boxColor || "#333" }}
        >
          <img
            src={`/assets/icons/boxes/${boxIcon}.svg`}
            className="w-5 h-5 fill-white stroke-white drop-shadow-md"
            alt="Squad Icon"
          />
        </div>
      )}

      {/* Overlay to darken background slightly for readability */}
      <div className="absolute inset-0 bg-black/20" />

      {/* Top Header: Name, ID, Level */}
      <div className="relative z-10 px-1 flex justify-between items-start w-full mb-2">
        <div className="flex flex-col items-start">
          <StrokedText
            className="text-lg font-black tracking-wide text-white capitalize"
            strokeColor="#000"
            strokeWidth={8}
            fontSize={16}
          >
            {pokemon.name ??
              pokemon.base_pokemon_name.toLowerCase().replace(/_/g, " ")}
          </StrokedText>
          <StrokedText
            className="text-xs font-bold text-gray-300"
            strokeColor="#222222"
            strokeWidth={5}
            fontSize={10}
          >
            #{pokemon.display_id.slice(0, 10)}
          </StrokedText>
        </div>

        <div className="flex items-center">
          <StrokedText
            className="text-xs font-bold text-pk-gray-2 mr-1"
            strokeColor="#000"
            strokeWidth={4}
            fontSize={9}
          >
            Lv
          </StrokedText>
          <StrokedText
            className="text-xl font-black text-white"
            strokeColor="#000"
            strokeWidth={8}
            fontSize={16}
          >
            {level.toString()}
          </StrokedText>
        </div>
      </div>

      {/* Pokemon Portrait */}
      <div className="relative z-10 flex items-center justify-center ">
        <div className="relative w-32 h-16">
          <PokemonSprite
            pokemon={pokemon}
            style={{
              scale: 2.5,
              position: "absolute",
              left: "50%",
              transform: "translateX(-30%)",
            }}
            showShadow
          />
        </div>
      </div>

      {/* Middle Stats */}
      <div className="relative z-10 flex justify-center gap-2 mb-1">
        <StatBox label="AP" value={stats.ability_power} />
        <StatBox label="RES" value={stats.resistance} />
        <StatBox label="DEX" value={stats.speed} />
      </div>

      {/* Active Skills & Talents Area */}
      <div className="relative flex flex-row gap-1 z-10  bg-black/50 rounded-lg p-2 backdrop-blur-md border border-white/10">
        {/* Active Skills */}
        <div className="flex flex-col gap-1 h-full justify-between w-0 flex-1">
          {pokemon.active_skills && pokemon.active_skills.length > 0 ? (
            pokemon.active_skills.map((skill, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded-full bg-pk-magenta/80 border border-white/30 flex items-center justify-center">
                  <div
                    className="w-2 h-2  rotate-45"
                    style={{
                      backgroundColor: typeColors[primaryType],
                    }}
                  />
                </div>
                <span className="text-[8px] text-white text-nowrap truncate font-medium capitalize shadow-black drop-shadow-sm">
                  {skill.toString().toLowerCase().replace(/_/g, " ")}
                </span>
              </div>
            ))
          ) : (
            <span className="text-[10px] text-gray-400 italic">
              No Active Skills
            </span>
          )}
        </div>

        {/* Talents */}
        {pokemon.talents && pokemon.talents.length > 0 && (
          <div className="grid grid-cols-3 gap-0 ml-auto min-w-12">
            {pokemon.talents.map((talent, i) => (
              <div key={i} className="relative group/talent">
                <img
                  src={`/assets/talents/${talent.toLowerCase()}.png`}
                  alt={talent.toString()}
                  className="w-6 h-6  drop-shadow-md opacity-90 hover:opacity-100 transition-opacity"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Holo Effect (kept from original) */}
      <div
        className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-30 mix-blend-overlay"
        style={{
          backgroundSize: "200% 200%",
          animation: "shimmer 3s infinite linear",
        }}
      />
    </div>
  );
};
